import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useFinance } from "@/context/FinanceContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Receipt, DollarSign } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const ClientCredit: React.FC = () => {
  const { t } = useTranslation();
  const { clients, clientTransactions, addClient, addClientTransaction, loading } = useFinance();
  const [clientOpen, setClientOpen] = useState(false);
  const [transactionOpen, setTransactionOpen] = useState(false);

  const [clientForm, setClientForm] = useState({
    name: "",
    phone: "",
    email: "",
    creditLimit: "",
  });

  const [transactionForm, setTransactionForm] = useState({
    clientId: "",
    type: "purchase" as "credit" | "payment" | "purchase",
    amount: "",
    description: "",
  });

  const handleAddClient = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await addClient({
        name: clientForm.name,
        phone: clientForm.phone,
        email: clientForm.email,
        creditLimit: parseFloat(clientForm.creditLimit),
      });
      setClientForm({ name: "", phone: "", email: "", creditLimit: "" });
      setClientOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleAddTransaction = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await addClientTransaction({
        clientId: transactionForm.clientId,
        type: transactionForm.type,
        amount: parseFloat(transactionForm.amount),
        description: transactionForm.description,
      });
      setTransactionForm({ clientId: "", type: "purchase", amount: "", description: "" });
      setTransactionOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  const totalCredit = clients.reduce((sum, c) => sum + c.currentBalance, 0);

  return (
    <div className="space-y-4">
      <Tabs defaultValue="clients">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="clients">{t("finance.clients")}</TabsTrigger>
          <TabsTrigger value="transactions">{t("finance.transactions")}</TabsTrigger>
        </TabsList>

        <TabsContent value="clients" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold">{t("finance.clients")}</h2>
              <p className="text-sm text-orange-600">{t("finance.totalCredit")}: {totalCredit.toFixed(2)} DT</p>
            </div>
            <Dialog open={clientOpen} onOpenChange={setClientOpen}>
              <DialogTrigger asChild>
                <Button><Plus className="h-4 w-4 mr-2" />{t("finance.addClient")}</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t("finance.addClient")}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddClient} className="space-y-4">
                  <div>
                    <Label>{t("finance.name")} *</Label>
                    <Input
                      value={clientForm.name}
                      onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>{t("finance.phone")} *</Label>
                    <Input
                      value={clientForm.phone}
                      onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>{t("finance.email")}</Label>
                    <Input
                      type="email"
                      value={clientForm.email}
                      onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>{t("finance.creditLimit")} (DT) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={clientForm.creditLimit}
                      onChange={(e) => setClientForm({ ...clientForm, creditLimit: e.target.value })}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full">{t("finance.addClient")}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardContent className="pt-6">
              {loading ? (
                <p>Loading...</p>
              ) : clients.length === 0 ? (
                <p className="text-gray-500">No clients added</p>
              ) : (
                <div className="space-y-2">
                  {clients.map((client) => (
                    <div key={client.id} className="p-3 border rounded">
                      <div className="flex justify-between">
                        <div>
                          <p className="font-medium">{client.name}</p>
                          <p className="text-sm text-gray-600">{client.phone}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Limit: {client.creditLimit.toFixed(2)} DT</p>
                          <p className={`font-semibold ${client.currentBalance > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                            Balance: {client.currentBalance.toFixed(2)} DT
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="transactions" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">{t("finance.transactions")}</h2>
            <Dialog open={transactionOpen} onOpenChange={setTransactionOpen}>
              <DialogTrigger asChild>
                <Button><Receipt className="h-4 w-4 mr-2" />{t("finance.newTransaction")}</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t("finance.newTransaction")}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddTransaction} className="space-y-4">
                  <div>
                    <Label>{t("finance.clients")} *</Label>
                    <Select value={transactionForm.clientId} onValueChange={(v) => setTransactionForm({ ...transactionForm, clientId: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t("finance.type")} *</Label>
                    <Select value={transactionForm.type} onValueChange={(v: any) => setTransactionForm({ ...transactionForm, type: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="purchase">{t("finance.purchase")}</SelectItem>
                        <SelectItem value="payment">{t("finance.payment")}</SelectItem>
                        <SelectItem value="credit">{t("finance.creditAdjustment")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t("finance.amount")} (DT) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={transactionForm.amount}
                      onChange={(e) => setTransactionForm({ ...transactionForm, amount: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>{t("finance.description")} *</Label>
                    <Input
                      value={transactionForm.description}
                      onChange={(e) => setTransactionForm({ ...transactionForm, description: e.target.value })}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full">{t("finance.newTransaction")}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardContent className="pt-6">
              {loading ? (
                <p>Loading...</p>
              ) : clientTransactions.length === 0 ? (
                <p className="text-gray-500">No transactions recorded</p>
              ) : (
                <div className="space-y-2">
                  {clientTransactions.map((transaction) => (
                    <div key={transaction.id} className="p-3 border rounded">
                      <div className="flex justify-between">
                        <div>
                          <p className="font-medium">{transaction.client?.name}</p>
                          <p className="text-sm text-gray-600">{transaction.description}</p>
                          {transaction.receiptNumber && (
                            <p className="text-xs text-gray-500">Receipt: {transaction.receiptNumber}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className={`font-semibold ${
                            transaction.type === 'payment' ? 'text-green-600' : 'text-orange-600'
                          }`}>
                            {transaction.type === 'payment' ? '-' : '+'}{transaction.amount.toFixed(2)} DT
                          </p>
                          <p className="text-xs text-gray-500">{transaction.createdAt.toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ClientCredit;
